import { apolloClient } from "@/apollo/index";

import resizeMasterTemplateMutation from "@/apollo/mutations/ResizeMasterTemplate.gql";
import campaignEditorQuery from "@/apollo/queries/v2/CampaignEditor.gql";

import type { InDesignMasterTemplate, MasterTemplate } from "hoxton-types";

import { requestIndesignRenditionOutput } from "@/utils/CreativeIntelligenceApi/indesign";

const INDESIGN_RENDITION_RESOURCE_TYPE = "ADOBE_INDESIGN_PREVIEW";

export type InDesignGeneratedSourceDocument = {
    bucket: string;
    key: string;
    outputFileName?: string;
    outputFormat?: string;
};

export async function generateInDesignSourceDocument({
    clientId,
    masterTemplate,
    width,
    height
}: {
    clientId: string;
    masterTemplate: InDesignMasterTemplate;
    width: number;
    height: number;
}) {
    if (!masterTemplate.sourceDocuments?.length) {
        throw new Error("No persisted source documents available for InDesign resize");
    }

    const output = await requestIndesignRenditionOutput({
        clientId,
        sourceDocuments: masterTemplate.sourceDocuments,
        renditionRequestBody: {
            type: INDESIGN_RENDITION_RESOURCE_TYPE,
            input: {
                clientId,
                sourceDocuments: masterTemplate.sourceDocuments.map(sourceDocument => ({
                    bucket: sourceDocument.bucket,
                    key: sourceDocument.key
                })),
                outputFormat: "indd",
                targetSize: {
                    width,
                    height,
                    unit: masterTemplate.measurementUnit
                },
                sharedOverrides: masterTemplate.defaultSharedOverrides ?? []
            }
        },
        missingSourceDocumentsMessage: "No persisted source documents available for InDesign resize",
        failureMessage: "Failed to generate resized InDesign variant"
    });
    const generatedSourceDocument = output?.s3FileLocations[0];

    if (!generatedSourceDocument) {
        throw new Error("InDesign rendition did not return a generated source document");
    }

    return generatedSourceDocument;
}

export async function resizeMasterTemplate({
    width,
    height,
    clientId,
    masterTemplate,
    masterTemplateId,
    campaignId,
    sandboxMode
}: {
    width: number;
    height: number;
    clientId: string;
    masterTemplate: MasterTemplate;
    masterTemplateId: string;
    campaignId: string;
    sandboxMode: boolean;
}) {
    const generatedSourceDocument =
        masterTemplate.adType === "INDESIGN"
            ? await generateInDesignSourceDocument({
                  clientId,
                  masterTemplate,
                  width,
                  height
              })
            : undefined;

    const { data, errors } = await apolloClient.mutate<{
        resizeMasterTemplate: MasterTemplate;
    }>({
        mutation: resizeMasterTemplateMutation,
        variables: {
            width,
            height,
            masterTemplateId,
            campaignId,
            generatedSourceDocument
        },
        context: {
            useLocalDevLink: sandboxMode
        },
        refetchQueries: [
            {
                query: campaignEditorQuery,
                variables: {
                    campaignId
                },
                context: {
                    useLocalDevLink: sandboxMode
                }
            }
        ]
    });

    if (errors?.length) {
        throw new Error(errors.map(e => e.message).join(", "));
    }

    if (!data?.resizeMasterTemplate) {
        throw new Error(`Failed to resize master template. Please try again.`);
    }

    return data.resizeMasterTemplate;
}
